import { useEffect, useState } from 'react';
import { mensajeDeError } from '@/core/red/conectividad';
import { Chips } from '@/ui/Chips';
import { ConfirmarBorrado } from '@/ui/ConfirmarBorrado';
import { MenuAcciones } from '@/ui/MenuAcciones';
import {
  CALCULATION_LABELS,
  createService,
  listServices,
  updateService,
  type Calculation,
  type OrganizationService,
  deleteService,
} from './catalog';

interface Props {
  organizationId: string;
  isAdmin: boolean;
}

/**
 * Tarifario de la organización.
 *
 * Los honorarios se expresan SIEMPRE en kg de novillo: no existe cobro directo
 * en pesos. El catálogo sugerido llegó como copia editable al crear la
 * organización; a partir de ahí es completamente propio.
 */
/** Dos opciones fijas: se eligen con chips, no abriendo un desplegable. */
const OPCIONES_CALCULO: { value: Calculation; label: string }[] = [
  { value: 'per_unit', label: CALCULATION_LABELS.per_unit },
  { value: 'per_job', label: CALCULATION_LABELS.per_job },
];

export function ServicesCard({ organizationId, isAdmin }: Props) {
  const [services, setServices] = useState<OrganizationService[]>([]);
  const [editando, setEditando] = useState<string | null>(null);
  const [borrador, setBorrador] = useState<{ name: string; kg: string; calc: Calculation } | null>(
    null,
  );
  const [nuevo, setNuevo] = useState({ name: '', kg: '', calc: 'per_unit' as Calculation });
  // Plegado por omisión: el formulario compite con la lista, y agregar es
  // frecuente pero no es a lo que se entra la mayoría de las veces.
  const [agregando, setAgregando] = useState(false);
  const [borrando, setBorrando] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recarga, setRecarga] = useState(0);
  // Veinticuatro conceptos entran en la pantalla pero no en la cabeza: con el
  // tarifario completo, encontrar «Tacto preservicio» era bajar y leer.
  const [busqueda, setBusqueda] = useState('');

  useEffect(() => {
    let cancelado = false;
    void (async () => {
      try {
        const lista = await listServices(organizationId);
        if (!cancelado) setServices(lista);
      } catch (e) {
        if (!cancelado) setError(mensajeDeError(e));
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [organizationId, recarga]);

  // Sin acentos y sin distinguir mayúsculas: quien busca «raspaje» tiene que
  // encontrar «1° raspaje y revisión» sin pelearse con la tilde.
  const normalizar = (t: string) => t.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const termino = normalizar(busqueda.trim());
  const visibles =
    termino === '' ? services : services.filter((x) => normalizar(x.name).includes(termino));

  const guardar = async (id: string) => {
    if (!borrador) return;
    setError(null);
    try {
      await updateService(id, {
        name: borrador.name.trim(),
        kg_novillo: Number(borrador.kg),
        calculation: borrador.calc,
      });
      setEditando(null);
      setBorrador(null);
      setRecarga((n) => n + 1);
    } catch (e) {
      setError(mensajeDeError(e));
    }
  };

  const alternar = async (s: OrganizationService) => {
    setError(null);
    try {
      await updateService(s.id, { is_active: !s.is_active });
      setRecarga((n) => n + 1);
    } catch (e) {
      setError(mensajeDeError(e));
    }
  };

  const borrar = async (id: string) => {
    setError(null);
    try {
      await deleteService(id);
      setBorrando(null);
      setRecarga((n) => n + 1);
    } catch (e) {
      setError(mensajeDeError(e));
    }
  };

  const agregar = async () => {
    setError(null);
    const kg = Number(nuevo.kg);
    if (nuevo.name.trim() === '' || !Number.isFinite(kg) || kg < 0) {
      setError('Poné un nombre y los kg de novillo');
      return;
    }
    try {
      await createService({
        organizationId,
        name: nuevo.name,
        kgNovillo: kg,
        calculation: nuevo.calc,
      });
      setNuevo({ name: '', kg: '', calc: 'per_unit' });
      // Se cierra al guardar: dejarlo abierto y vacío hace dudar de si se
      // guardó. La lista de abajo, ya actualizada, es la confirmación.
      setAgregando(false);
      setRecarga((n) => n + 1);
    } catch (e) {
      setError(mensajeDeError(e));
    }
  };

  return (
    // Sin caja ni título propio: el encabezado de la pantalla ya dice
    // «Honorarios», y repetirlo adentro de un rectángulo hacía parecer que
    // eran dos cosas distintas. La aclaración de cómo se cobran queda, en
    // gris, donde se lee antes de tocar nada.
    <section className="flex flex-col gap-3" data-testid="services-card">
      <p className="text-sm text-[var(--tinta-suave)]">
        Se cobran en kg de novillo. Podés cambiar nombre, kg y tipo de cálculo cuando quieras.
      </p>

      {error && (
        <p role="alert" className="mb-2 text-sm text-[var(--peligro)]" data-testid="services-error">
          {error}
        </p>
      )}

      {/* Agregar va ARRIBA. Estaba al final de la lista, y con veinticuatro
          honorarios cargados eso significaba recorrer la pantalla entera cada
          vez para hacer algo que se hace seguido. La lista se recorre para
          consultar; el formulario se busca para actuar, y lo que se busca no
          puede estar escondido debajo de lo que se hojea. */}
      {isAdmin && !agregando && (
        <button
          type="button"
          onClick={() => setAgregando(true)}
          className="mb-3 min-h-11 w-full rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] border-dashed text-sm font-medium"
          data-testid="add-service-open"
        >
          ＋ Agregar honorario
        </button>
      )}

      {isAdmin && agregando && (
        <div
          className="mb-3 flex flex-col gap-2 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] p-3"
          data-testid="add-service"
        >
          <p className="text-sm font-medium">Agregar honorario</p>
          <input
            placeholder="Nombre"
            value={nuevo.name}
            onChange={(e) => setNuevo({ ...nuevo, name: e.target.value })}
            className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3"
            data-testid="new-service-name"
          />
          <div className="flex gap-2">
            <input
              type="number"
              min="0"
              step="0.001"
              placeholder="kg"
              value={nuevo.kg}
              onChange={(e) => setNuevo({ ...nuevo, kg: e.target.value })}
              className="min-h-11 w-28 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3"
              data-testid="new-service-kg"
            />
            <Chips
              value={nuevo.calc}
              onChange={(calc) => setNuevo({ ...nuevo, calc })}
              options={OPCIONES_CALCULO}
              testId="new-service-calc"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => void agregar()}
              className="min-h-11 rounded-[var(--radius-control)] bg-[var(--accion)] px-4 text-sm text-[var(--sobre-accion)]"
              data-testid="new-service-submit"
            >
              Agregar
            </button>
            <button
              type="button"
              onClick={() => setAgregando(false)}
              className="min-h-11 px-2 text-sm underline opacity-70"
              data-testid="add-service-cancel"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      <input
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        placeholder="Buscar honorario…"
        className="mb-3 min-h-11 w-full rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-base"
        data-testid="services-buscar"
      />

      <ul className="flex flex-col divide-y divide-[var(--linea)]" data-testid="services-list">
        {visibles.length === 0 && busqueda.trim() !== '' && (
          <li
            className="py-3 text-sm text-[var(--tinta-suave)]"
            data-testid="services-sin-resultados"
          >
            Ningún honorario coincide con «{busqueda.trim()}».
          </li>
        )}
        {visibles.map((s) => (
          <li key={s.id} className="py-3" data-testid="service-row">
            {editando === s.id && borrador ? (
              <div className="flex flex-col gap-2">
                <input
                  value={borrador.name}
                  onChange={(e) => setBorrador({ ...borrador, name: e.target.value })}
                  className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3"
                  data-testid="edit-name"
                />
                <div className="flex gap-2">
                  <input
                    type="number"
                    min="0"
                    step="0.001"
                    value={borrador.kg}
                    onChange={(e) => setBorrador({ ...borrador, kg: e.target.value })}
                    className="min-h-11 w-28 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3"
                    data-testid="edit-kg"
                  />
                  <Chips
                    value={borrador.calc}
                    onChange={(calc) => setBorrador({ ...borrador, calc })}
                    options={OPCIONES_CALCULO}
                    testId="edit-calc"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => void guardar(s.id)}
                    className="min-h-11 rounded-[var(--radius-control)] bg-[var(--accion)] px-4 text-sm text-[var(--sobre-accion)]"
                    data-testid="edit-save"
                  >
                    Guardar
                  </button>
                  <button
                    onClick={() => {
                      setEditando(null);
                      setBorrador(null);
                    }}
                    className="min-h-11 px-2 text-sm underline opacity-70"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p
                    className={`truncate font-medium ${s.is_active ? '' : 'text-[var(--tinta-tenue)]'}`}
                  >
                    {s.name}
                    {!s.is_active && <span className="ml-2 text-xs">(inactivo)</span>}
                  </p>
                  <p className="text-sm text-[var(--tinta-suave)]">
                    {s.kg_novillo} kg · {CALCULATION_LABELS[s.calculation]}
                  </p>
                </div>
                {/* Un menú, no tres botones por renglón. Con treinta honorarios
                    eran noventa botones: la pantalla dejaba de ser una lista de
                    honorarios para ser una lista de acciones, y encontrar el
                    honorario costaba más que editarlo. */}
                {isAdmin && (
                  <MenuAcciones
                    etiqueta={`Acciones de ${s.name}`}
                    testId="service-menu"
                    acciones={[
                      {
                        etiqueta: 'Editar',
                        testId: 'edit-service',
                        onClick: () => {
                          setEditando(s.id);
                          setBorrador({
                            name: s.name,
                            kg: String(s.kg_novillo),
                            calc: s.calculation,
                          });
                        },
                      },
                      {
                        // Desactivar y borrar son cosas distintas: desactivar
                        // saca del listado un honorario que ya no se ofrece;
                        // borrar es para el que nunca debió existir. Los
                        // trabajos viejos no se enteran: cada línea guardó su
                        // nombre y sus kg el día que se cargó.
                        etiqueta: s.is_active ? 'Desactivar' : 'Activar',
                        testId: 'toggle-service',
                        onClick: () => void alternar(s),
                      },
                      {
                        etiqueta: 'Eliminar',
                        testId: 'delete-service',
                        destructiva: true,
                        onClick: () => setBorrando(s.id),
                      },
                    ]}
                  />
                )}
              </div>
            )}
          </li>
        ))}
      </ul>

      {!isAdmin && (
        <p className="mt-3 text-xs text-[var(--tinta-tenue)]" data-testid="services-readonly">
          Solo un administrador puede modificar el tarifario.
        </p>
      )}
      {borrando && (
        <ConfirmarBorrado
          que={`el honorario «${services.find((x) => x.id === borrando)?.name ?? ''}»`}
          detalle="Los trabajos ya cargados no cambian: guardaron su nombre y sus kg."
          onCancelar={() => setBorrando(null)}
          onConfirmar={() => void borrar(borrando)}
          testId="confirmar-borrar-honorario"
        />
      )}
    </section>
  );
}
